import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
} from '../components/ui';
import { UserProfile } from '../components/profile';
import { ManageSubscription } from '../components/subscription';
import { useAuth } from '../stores/auth.store';

// Mock user's high scores (will come from API later)
const mockUserScores = [
  {
    gameId: 'snake',
    gameName: 'Snake',
    score: 888888,
    rank: 2,
    date: '2026-01-14',
  },
  {
    gameId: 'falling-blocks',
    gameName: 'Falling Blocks',
    score: 500000,
    rank: 1,
    date: '2026-01-12',
  },
  {
    gameId: 'wall-breaker',
    gameName: 'Wall Breaker',
    score: 200000,
    rank: 3,
    date: '2026-01-08',
  },
  {
    gameId: 'pill-muncher',
    gameName: 'Pill Muncher',
    score: 350000,
    rank: 5,
    date: '2026-01-05',
  },
];

// Mock achievements (will come from API later)
const mockAchievements = [
  {
    id: 'first-game',
    name: 'First Steps',
    description: 'Play your first game',
    unlocked: true,
    icon: '🎯',
  },
  {
    id: 'high-scorer',
    name: 'High Scorer',
    description: 'Reach top 10 on any leaderboard',
    unlocked: true,
    icon: '🏆',
  },
  {
    id: 'marathon',
    name: 'Marathon Gamer',
    description: 'Play 100 games',
    unlocked: true,
    icon: '🏃',
  },
  {
    id: 'perfectionist',
    name: 'Perfectionist',
    description: 'Get #1 on a leaderboard',
    unlocked: true,
    icon: '👑',
  },
  {
    id: 'social',
    name: 'Social Butterfly',
    description: 'Challenge 10 friends',
    unlocked: false,
    icon: '🦋',
  },
  {
    id: 'collector',
    name: 'Game Collector',
    description: 'Play all available games',
    unlocked: false,
    icon: '📚',
  },
];

export function ProfilePage() {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();

  // If not authenticated, show sign-in prompt
  if (!isAuthenticated || !user) {
    return (
      <div className="container mx-auto px-4 py-16">
        <Card className="max-w-md mx-auto text-center">
          <CardContent className="py-12">
            <div className="text-6xl mb-4">🔒</div>
            <h2 className="text-xl font-bold text-white mb-2">
              Sign In Required
            </h2>
            <p className="text-slate-400 mb-6">
              Please sign in to view your profile, stats, and achievements.
            </p>
            <Button onClick={() => navigate('/')}>Go to Home</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main content - Profile Component */}
        <div className="lg:col-span-2">
          <Card>
            <CardContent className="py-6">
              <UserProfile />
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Personal High Scores */}
          <Card>
            <CardHeader>
              <CardTitle>Your High Scores</CardTitle>
            </CardHeader>
            <CardContent>
              {mockUserScores.length > 0 ? (
                <div className="space-y-3">
                  {mockUserScores.map((score) => (
                    <div
                      key={score.gameId}
                      className="flex items-center justify-between py-2 border-b border-slate-700/50 last:border-0"
                    >
                      <div>
                        <div className="text-white font-medium">
                          {score.gameName}
                        </div>
                        <div className="text-xs text-slate-400">
                          {score.date}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-purple-400 font-mono">
                          {score.score.toLocaleString()}
                        </div>
                        <div className="text-xs text-slate-400">
                          Rank #{score.rank}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-400 text-sm">
                  No high scores yet. Start playing!
                </p>
              )}
            </CardContent>
          </Card>

          {/* Subscription */}
          <ManageSubscription />

          {/* Achievements */}
          <Card>
            <CardHeader>
              <CardTitle>
                Achievements (
                {mockAchievements.filter((a) => a.unlocked).length}/
                {mockAchievements.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {mockAchievements.map((achievement) => (
                  <div
                    key={achievement.id}
                    className={`flex items-center gap-3 p-2 rounded-lg ${
                      achievement.unlocked
                        ? 'bg-slate-700/50'
                        : 'bg-slate-800/30 opacity-50'
                    }`}
                  >
                    <div className="text-xl">{achievement.icon}</div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-white text-sm truncate">
                        {achievement.name}
                      </div>
                      <div className="text-xs text-slate-400 truncate">
                        {achievement.description}
                      </div>
                    </div>
                    {achievement.unlocked && (
                      <span className="text-green-400 text-sm flex-shrink-0">
                        ✓
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
